/**
 * Minimal structural shape these helpers need from a chat message. Keeps the
 * helper independent of which `ScoutChatMessage` flavor a caller uses (the
 * `hooks/types` strict version vs. the `Chat/message/scoutChatMessage`
 * optional-content override).
 */
type ChatMessageLike = {
  role: "system" | "user" | "assistant";
  parts: ReadonlyArray<{ type: string; text?: string | null; url?: string | null }>;
};

const SCOUT_PRIVATE_DOCUMENT_EXTRACTION_URL_PREFIX = "encamp-private-document-extraction://";

function hasExtractionWatchFilePart(message: ChatMessageLike): boolean {
  return message.parts.some(
    (part) =>
      part.type === "file" &&
      typeof part.url === "string" &&
      part.url.startsWith(SCOUT_PRIVATE_DOCUMENT_EXTRACTION_URL_PREFIX)
  );
}

/**
 * The UI auto-sends a "synthetic" user message after the user clicks a button
 * on an inline confirmation card (currently only the tenant document-library
 * proposal). The synthetic message gives Scout's next turn enough context to
 * proceed without re-prompting the user; the chat renderer hides it so the
 * user doesn't see their own click rephrased as a chat bubble.
 *
 * **Why a text-prefix sentinel and not a typed enum field on the message:**
 * `ScoutMessagePart` is persisted server-side and shared across many ingest
 * paths; adding an enum column for "synthetic event kind" would require a
 * schema migration plus updates to every materializer. The prefix approach
 * keeps the change contained to client + prompt and round-trips cleanly
 * through the existing string-typed message body. The trade-off is fragility
 * (if anyone ever types the exact prefix, they'd disappear from the UI), so
 * the prefix is intentionally implausible-looking.
 *
 * If we add a second kind of synthetic event, this is the right place to add
 * an enum-style discriminator inside the prefix line.
 */
export const SCOUT_INTERNAL_EVENT_PREFIX = "[scout-internal-event]";

export type TenantDocumentProposalEventStatus = "confirmed" | "declined";

const TENANT_DOCUMENT_PROPOSAL_EVENT_PATTERN =
  /^\[scout-internal-event\]\s+document-library-proposal\/(confirmed|declined)\s+proposalId=([^\s:]+)/;

function pluralizeDocument(count: number): string {
  return count === 1 ? "document" : "documents";
}

/**
 * Build the synthetic user message text for a tenant document-library
 * proposal action. Scout sees this in its context and uses the
 * confirmed/declined signal to decide what to do next; the chat UI hides it.
 */
export function buildTenantDocumentProposalEventMessage(input: {
  status: TenantDocumentProposalEventStatus;
  proposalId: string;
  /** Documents the user approved (confirm path) or the full set declined. */
  count: number;
  /** Original total proposed; used to note "of M proposed" when partial. */
  totalProposedCount: number;
}): string {
  const fileWord = pluralizeDocument(input.count);
  if (input.status === "confirmed") {
    const proposedNote =
      input.count === input.totalProposedCount ? "" : ` (of ${input.totalProposedCount} proposed)`;
    return (
      `${SCOUT_INTERNAL_EVENT_PREFIX} document-library-proposal/confirmed ` +
      `proposalId=${input.proposalId}: ` +
      `${input.count} ${fileWord}${proposedNote}. ` +
      `Continue using the newly attached documents to answer the user's previous question.`
    );
  }
  return (
    `${SCOUT_INTERNAL_EVENT_PREFIX} document-library-proposal/declined ` +
    `proposalId=${input.proposalId}: ` +
    `${input.totalProposedCount} ${fileWord}. ` +
    `Proceed with the alternative you offered. Do not re-propose the same set.`
  );
}

/**
 * Returns true if the chat UI should drop the whole message from rendering.
 *
 * Synthetic confirm messages for large-PDF ingest carry `extraction-watch`
 * file parts; those messages must STAY visible because `UserMessage` reads
 * the file parts to render the inline extraction progress card. The bubble
 * text is stripped separately by `stripScoutInternalEventLineFromText`, so
 * what the user sees is just the progress card.
 *
 * Synthetic decline messages and small-doc confirms have no file parts and
 * nothing useful to render — those we drop entirely.
 */
export function isSyntheticInternalEventMessage(message: ChatMessageLike): boolean {
  if (message.role !== "user") return false;
  const hasSentinelText = message.parts.some(
    (part) => part.type === "text" && part.text?.trimStart().startsWith(SCOUT_INTERNAL_EVENT_PREFIX)
  );
  if (!hasSentinelText) return false;
  return !hasExtractionWatchFilePart(message);
}

/**
 * Strips the `[scout-internal-event] …` sentinel line from a text part. Used
 * by `UserMessage` so a synthetic confirm message with extraction-watch
 * attachments renders only the progress card — never the raw event text.
 */
export function stripScoutInternalEventLineFromText(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(/^\[scout-internal-event\][^\n]*\n?/gm, "").trim();
}

/**
 * Scan the persisted conversation history for tenant document-library
 * proposal events and return a map of `proposalId -> status`. Lets the
 * proposal card stay in its "responded" state after a page refresh without
 * needing a dedicated DB column — the responded record is the synthetic
 * user message itself.
 */
export function parseRespondedTenantDocumentProposalsFromMessages(
  messages: ReadonlyArray<ChatMessageLike>
): Record<string, TenantDocumentProposalEventStatus> {
  const responded: Record<string, TenantDocumentProposalEventStatus> = {};
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const part of message.parts) {
      if (part.type !== "text" || typeof part.text !== "string") continue;
      const match = part.text.trimStart().match(TENANT_DOCUMENT_PROPOSAL_EVENT_PATTERN);
      if (!match) continue;
      const status: TenantDocumentProposalEventStatus =
        match[1] === "confirmed" ? "confirmed" : "declined";
      const proposalId = match[2];
      responded[proposalId] = status;
    }
  }
  return responded;
}
